import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import classnames from 'classnames';
import { Stack, Headline, BodyText, Eyebrow, Button } from '@servicetitan/design-system';
import Animated from 'animated/lib/targets/react-dom';
import SwipeableViews, { OnSwitchingCallbackTypeDescriptor } from 'react-swipeable-views';
import { PaginationDots } from 'src/core/components/pagination/pagination-dots';
import { PlanCard } from './plan-card';
import { CalculatedProgram } from 'src/api';
import * as Utils from '../../forms/application-information-forms-utils';

import styles from './plan-details.less';

interface IProps {
    loanAmount: string;
    isLoanAmountValid: boolean;
    plans: CalculatedProgram[];
    selectedPlan?: CalculatedProgram;
    selectPlan(planNumber: string): void;
    unSelectPlan(planNumber: string): void;
}

export const PlanDetails: React.FC<IProps> = observer(
    ({ loanAmount, isLoanAmountValid, plans, selectedPlan, selectPlan, unSelectPlan }) => {
        const [selectedIndex, setSelectedIndex] = useState(0);
        const position = new Animated.Value(0);

        const handleSwitch = (index: number, type: OnSwitchingCallbackTypeDescriptor) => {
            if (type === 'end') {
                Animated.spring(position, { toValue: index }).start();
                return;
            }
            position.setValue(index);
        };

        const handleChangeIndex = (index: number) => {
            setSelectedIndex(index);
            Animated.spring(position, { toValue: index }).start();
        };

        const isSelected = (planNumber: string) =>
            selectedPlan ? selectedPlan.planNumber === planNumber : false;

        const isDisabled = (plan: CalculatedProgram) => {
            const needToBeDisabled = parseFloat(loanAmount) < plan.minLoanAmount;
            if (needToBeDisabled) {
                unSelectPlan(plan.planNumber!);
            }
            return needToBeDisabled;
        };

        return (
            <>
                <Stack direction="column" justifyContent="flex-start">
                    <Stack.Item alignSelf="center" className={styles.center}>
                        <BodyText size="xsmall" className="m-b-half">
                            Desired Loan Amount
                        </BodyText>
                        <Headline
                            size="xlarge"
                            className={classnames({ [styles.invalidLoan]: !isLoanAmountValid })}
                        >
                            ${loanAmount}
                        </Headline>
                        <BodyText subdued size="xsmall" className="m-b-half">
                            Subject to approval by Service Finance
                        </BodyText>
                    </Stack.Item>
                    <Stack.Item>
                        <SwipeableViews
                            index={selectedIndex}
                            onSwitching={handleSwitch}
                            onChangeIndex={handleChangeIndex}
                            enableMouseEvents
                            style={{ overflowX: 'inherit' }}
                            slideStyle={{ overflow: 'inherit' }}
                        >
                            {plans.map((plan, index) => {
                                return (
                                    <Animated.div key={index}>
                                        <div className={styles.planCard}>
                                            <PlanCard plan={plan} isDisabled={isDisabled} />
                                            {!isDisabled(plan) && (
                                                <React.Fragment>
                                                    <hr />
                                                    <div className={styles.planButton}>
                                                        <Button
                                                            className={classnames(
                                                                styles.selectPlan,
                                                                {
                                                                    [styles.planSelected]:
                                                                        isSelected(
                                                                            plan.planNumber!
                                                                        ),
                                                                }
                                                            )}
                                                            primary
                                                            onClick={() =>
                                                                isSelected(plan.planNumber!)
                                                                    ? unSelectPlan(plan.planNumber!)
                                                                    : selectPlan(plan.planNumber!)
                                                            }
                                                        >
                                                            {isSelected(plan.planNumber!)
                                                                ? 'Selected'
                                                                : 'Select'}
                                                        </Button>
                                                    </div>
                                                </React.Fragment>
                                            )}
                                        </div>
                                    </Animated.div>
                                );
                            })}
                        </SwipeableViews>
                        <PaginationDots
                            className={styles.pagination}
                            small
                            dots={plans.length}
                            index={selectedIndex}
                            onChangeIndex={(index) => setSelectedIndex(index)}
                        />
                    </Stack.Item>
                </Stack>
            </>
        );
    }
);
